import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Responsive srcsets for category tiles (displayed at ~223-300px wide)
import categoryLightingSrc from "@/assets/category-lighting.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryLightingFallback from "@/assets/category-lighting.jpg?format=webp&quality=75&w=500";
import categoryElectricitySrc from "@/assets/category-electricity.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryElectricityFallback from "@/assets/category-electricity.jpg?format=webp&quality=75&w=500";
import categoryHardwareSrc from "@/assets/category-hardware.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryHardwareFallback from "@/assets/category-hardware.jpg?format=webp&quality=75&w=500";
import categoryKitchenSrc from "@/assets/category-kitchen.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryKitchenFallback from "@/assets/category-kitchen.jpg?format=webp&quality=75&w=500";
import categoryBathroomSrc from "@/assets/category-bathroom.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryBathroomFallback from "@/assets/category-bathroom.jpg?format=webp&quality=75&w=500";
import categoryLifestyleSrc from "@/assets/category-lifestyle.jpg?format=webp&quality=75&w=300;500;800&as=srcset";
import categoryLifestyleFallback from "@/assets/category-lifestyle.jpg?format=webp&quality=75&w=500";
import { buildCategoryPath } from "@/lib/catalogUrl";

export const Categories = () => {
  const { t, language, getLocalizedPath } = useLanguage();

  const getCategoryLabel = (key: string) => {
    const labels: Record<string, Record<string, string>> = {
      bathroom: { en: "Bathroom", lv: "Vannasistaba", ru: "Ванная" },
      kitchen: { en: "Kitchen", lv: "Virtuve", ru: "Кухня" },
      lighting: { en: "Lighting", lv: "Apgaismojums", ru: "Освещение" },
      electricity: { en: "Electricity", lv: "Elektrība", ru: "Электрика" },
      hardware: { en: "Hardware", lv: "Furnitūra", ru: "Фурнитура" },
      interiorLifestyle: { en: "Interior & Life Style", lv: "Interjers & Dzīvesstils", ru: "Интерьер и стиль жизни" },
    };
    return labels[key]?.[language] || labels[key]?.en || key;
  };

  const categories = [
    {
      key: "bathroom",
      title: getCategoryLabel("bathroom"),
      href: getLocalizedPath(buildCategoryPath("Bathroom")),
      image: categoryBathroomFallback,
      srcset: categoryBathroomSrc,
    },
    {
      key: "kitchen",
      title: getCategoryLabel("kitchen"),
      href: getLocalizedPath(buildCategoryPath("Kitchen")),
      image: categoryKitchenFallback,
      srcset: categoryKitchenSrc,
    },
    {
      key: "lighting",
      title: getCategoryLabel("lighting"),
      href: getLocalizedPath(buildCategoryPath("Lighting")),
      image: categoryLightingFallback,
      srcset: categoryLightingSrc,
    },
    {
      key: "electricity",
      title: getCategoryLabel("electricity"),
      href: getLocalizedPath(buildCategoryPath("Electricity")),
      image: categoryElectricityFallback,
      srcset: categoryElectricitySrc,
    },
    {
      key: "hardware",
      title: getCategoryLabel("hardware"),
      href: getLocalizedPath(buildCategoryPath("Hardware")),
      image: categoryHardwareFallback,
      srcset: categoryHardwareSrc,
    },
    {
      key: "interiorLifestyle",
      title: getCategoryLabel("interiorLifestyle"),
      href: getLocalizedPath(buildCategoryPath("Life Style")),
      image: categoryLifestyleFallback,
      srcset: categoryLifestyleSrc,
    },
  ];

  return (
    <section className="bg-charcoal">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6">
        {categories.map((category, index) => (
          <Link
            key={category.key}
            to={category.href}
            className="group relative aspect-[3/4] overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Background image */}
            <img 
              src={category.image}
              srcSet={category.srcset}
              sizes="(min-width: 1280px) 17vw, (min-width: 640px) 33vw, 50vw"
              alt={category.title}
              width={500}
              height={667}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
            
            {/* Content */}
            <div className="absolute inset-0 flex flex-col justify-end p-4 lg:p-6">
              <p 
                className="text-[10px] sm:text-sm lg:text-base xl:text-lg font-bold text-cream mb-2 lg:mb-4 uppercase leading-tight tracking-[0.08em] xl:tracking-wider break-normal [overflow-wrap:normal] group-hover:text-gold transition-colors duration-300"
                style={{ fontFamily: "'Michroma', sans-serif" }}
              >
                {category.title}
              </p>

              <div className="flex items-center gap-2 text-cream/50 group-hover:text-gold transition-colors duration-300">
                <span className="text-[10px] lg:text-xs font-medium uppercase tracking-wider">{t.hero.exploreRange}</span>
                <ArrowUpRight className="w-3 h-3 lg:w-4 lg:h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
